'use client';

import { type PropsWithChildren, Suspense } from 'react';

import dynamic from 'next/dynamic';

import { useSetAtom } from 'jotai';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';

import { useToast } from '@quick/components/Toaster';
import { UserProfiles } from '@quick/go-api-types/users/config';

import Arrow from '@/app/[lang]/(oppl)/operations/page';
import { useAppTranslation } from '@/app/i18n/client';
import { useLangProvider } from '@/components/core/AppLayout';
import { Separator } from '@/components/core/Separator';
import {
  Background,
  CommonForm,
  FillFromQuote,
  FormDataContext,
  type IForm,
  defaultValues,
  parentClassName,
  schema,
  submitHandler,
  useFormDataContext,
} from '@/components/demandForm';
import useFormParams from '@/components/demandForm/useFormParams';
import { useUser } from '@/layers/core/deal/services/useUser';
import { useUserInfo } from '@/layers/core/deal/services/useUserInfo';

import { loginAtom } from './DemandLayout/atoms';

const Quotation = dynamic(() => import('@/components/quotes'));

const clientProfiles: Set<number> = new Set([
  UserProfiles.cliente,
  UserProfiles.cliente_empresa,
  UserProfiles.administrador_empresa,
]);

export default function Home() {
  const { data } = useUserInfo();
  if (!data || data?.profiles.some((p) => clientProfiles.has(p.id))) {
    return (
      <Suspense>
        <ServiceForm />
      </Suspense>
    );
  }
  return <Arrow />;
}

function ServiceForm() {
  const { t } = useAppTranslation(['common', 'demand']);

  const form = useForm<IForm>({
    defaultValues,
  });

  return (
    <div
      className="bg-background-light dark:bg-background-dark flex h-full flex-col gap-8
        overflow-y-auto overflow-x-hidden px-4 py-10 sm:p-10"
    >
      <h1 className="z-10 text-center text-3xl dark:text-white">
        {t('demand:demandForm.getRates')}
      </h1>
      <Separator className="mx-auto w-2/3" />
      <FormProvider {...form}>
        <FormDataContext>
          <FillFromQuote />
          <Suspense>
            <Form>
              <CommonForm />
            </Form>
          </Suspense>
          <Quotation />
          <Background />
        </FormDataContext>
      </FormProvider>
    </div>
  );
}

function Form({ children }: PropsWithChildren) {
  const { t } = useAppTranslation(['common', 'demand']);
  const lang = useLangProvider();
  const [{ si }] = useFormParams();

  const { token, id } = useUser();
  const setLogin = useSetAtom(loginAtom);

  const { toast } = useToast();

  const { handleSubmit } = useFormContext<IForm>();
  const { serviceType } = useFormDataContext();
  const serviceAdditionals =
    serviceType?.additional_fields
      .filter(
        ({ field: { is_for_offer, is_package } }) => is_for_offer && !is_package
      )
      .map(({ field }) => field.id.toString()) ?? [];
  const useFirstStop = serviceType?.use_first_address;
  const onSubmit = handleSubmit(
    async (data) => {
      if (!token) return setLogin(true);
      await submitHandler({
        data: { ...data, user_id: id },
        validator: (valid) => schema.omit({ user_id: true }).parse(valid),
        toast,
        t,
        lang,
        token,
        aro: serviceType?.allow_receive_offers,
        si,
        serviceAdditionals,
        useFirstStop,
      });
    },
    (errors) => {
      toast({
        variant: 'warning',
        description: t('demand:demandForm.validate'),
      });
      // eslint-disable-next-line no-console
      if (typeof window !== 'undefined' && window.__Q_DF) console.error(errors);
    }
  );
  return (
    <form onSubmit={onSubmit} className={parentClassName}>
      {children}
    </form>
  );
}
